import React, { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { 
  setUserOnline, 
  setUserOffline, 
  subscribeToPresence, 
  initializePresence, 
  cleanupPresence,
  updateLastSeen
} from '../services/presence';
import { generateUserColor, generateDisplayName } from '../utils/helpers';

/**
 * Custom hook for managing user presence and online status
 * @returns {Array} onlineUsers - Array of currently online users
 */
// Debug: Track hook renders
let renderCount = 0;

export function usePresence() {
  renderCount++;
  console.log(`🔄 usePresence RENDER #${renderCount}`);

  const { currentUser } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState([]);

  // Extract stable userId to prevent effect re-runs
  const userId = currentUser?.uid;

  // Debug: Log when userId changes
  const prevUserIdRef = React.useRef(userId);
  React.useEffect(() => {
    if (prevUserIdRef.current !== userId) {
      console.log('⚠️ userId CHANGED:', {
        from: prevUserIdRef.current?.substring(0, 8) + '...',
        to: userId?.substring(0, 8) + '...',
        timestamp: new Date().toISOString()
      });
      prevUserIdRef.current = userId;
    }
  });

  useEffect(() => {
    console.log('🔥 EFFECT RUNNING - usePresence effect triggered!', {
      userId: userId?.substring(0, 8) + '...',
      timestamp: new Date().toISOString()
    });

    if (!userId) {
      // Clear online users when user logs out
      console.log('🔍 DEBUG - No current user, clearing presence');
      setOnlineUsers([]);
      return;
    }

    // Capture current user data at effect initialization to avoid stale closures
    const userEmail = currentUser?.email;
    const userDisplayName = currentUser?.displayName;

    console.log('🔍 DEBUG - Current user:', {
      uid: userId.substring(0, 8) + '...',
      email: userEmail,
      displayName: userDisplayName
    });

    let unsubscribe = null;
    let heartbeatInterval = null;

    const initializeUserPresence = async () => {
      try {
        // Generate consistent color and display name for current user
        const userColor = generateUserColor(userId);
        const displayName = generateDisplayName(currentUser);

        console.log('🔍 DEBUG - Initializing presence:', {
          uid: userId.substring(0, 8) + '...',
          displayName,
          color: userColor
        });

        // Initialize presence (includes onDisconnect setup)
        await initializePresence(userId, displayName, userColor);
        
        // Set up heartbeat - update lastSeen every 10 seconds
        heartbeatInterval = setInterval(async () => {
          try {
            await updateLastSeen(userId);
            // Silent heartbeat - only log errors
          } catch (error) {
            console.warn('Heartbeat failed:', error);
          }
        }, 10000); // Every 10 seconds
        
        // Subscribe to presence updates for all users
        unsubscribe = subscribeToPresence((updatedOnlineUsers) => {
          // Filter out stale users (lastSeen > 30 seconds ago)
          const now = Date.now();
          const staleThreshold = 30000; // 30 seconds
          
          const activeUsers = updatedOnlineUsers.filter(user => {
            const lastSeenTime = user.lastSeen || 0;
            const timeSinceLastSeen = now - lastSeenTime;
            const isActive = timeSinceLastSeen < staleThreshold;
            
            if (!isActive) {
              console.log('🧹 Filtering out stale user:', user.displayName, `(${Math.round(timeSinceLastSeen / 1000)}s ago)`);
            }
            
            return isActive;
          });
          
          console.log('🔍 DEBUG - Presence callback received:', {
            rawCount: updatedOnlineUsers.length,
            filteredCount: activeUsers.length,
            currentUserId: userId.substring(0, 8) + '...',
            users: activeUsers.map(u => ({
              id: u.userId.substring(0, 8) + '...',
              name: u.displayName,
              isCurrentUser: u.userId === userId,
              lastSeen: `${Math.round((now - (u.lastSeen || now)) / 1000)}s ago`
            }))
          });
          
          setOnlineUsers(activeUsers);
        });
        
        console.log('✅ Presence tracking initialized for:', displayName);
      } catch (error) {
        console.error('❌ Error initializing presence tracking:', error);
        // Set empty array on error to prevent app crash
        setOnlineUsers([]);
      }
    };

    initializeUserPresence();

    // Background cleanup job - mark truly stale users as offline in database
    const cleanupStaleUsers = async () => {
      try {
        const { ref: dbRef, get } = await import('firebase/database');
        const { rtdb } = await import('../services/firebase');
        const { RTDB_PATHS } = await import('../utils/constants');
        
        const sessionsRef = dbRef(rtdb, RTDB_PATHS.USER_SESSIONS);
        const snapshot = await get(sessionsRef);
        
        if (snapshot.exists()) {
          const data = snapshot.val();
          const now = Date.now();
          const cleanupThreshold = 60000; // 1 minute
          
          for (const [userId, userData] of Object.entries(data)) {
            if (userData.isOnline && userData.lastSeen) {
              const timeSinceLastSeen = now - userData.lastSeen;
              if (timeSinceLastSeen > cleanupThreshold) {
                console.log('🧹 Cleaning up stale user in database:', userData.displayName);
                await setUserOffline(userId);
              }
            }
          }
        }
      } catch (error) {
        console.warn('Stale user cleanup failed:', error);
      }
    };

    // Run cleanup every 30 seconds
    const cleanupInterval = setInterval(cleanupStaleUsers, 30000);

    // Cleanup function
    return () => {
      // Clear cleanup interval
      if (cleanupInterval) {
        clearInterval(cleanupInterval);
      }
      console.log('🔍 DEBUG - Cleaning up presence for user:', userId.substring(0, 8) + '...');
      
      // Clear heartbeat interval
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
      
      // Unsubscribe from presence updates
      if (unsubscribe) {
        unsubscribe();
      }
      
      // Manually set user offline (immediate cleanup)
      if (userId) {
        setUserOffline(userId).catch(error => {
          console.warn('Warning: Failed to set user offline:', error);
        });
        
        // Also call general cleanup
        cleanupPresence(userId).catch(error => {
          console.warn('Warning: Failed to cleanup presence:', error);
        });
      }
      
      console.log('🧹 Presence tracking cleaned up');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Only depend on userId to prevent effect re-runs on mouse move
    // currentUser is captured in closure at effect initialization
  }, [userId]);

  return onlineUsers;
}

/**
 * Hook variant that returns both online users and presence utilities
 * @returns {Object} { onlineUsers, onlineCount, isUserOnline }
 */
export function usePresenceWithUtils() {
  const onlineUsers = usePresence();
  
  const onlineCount = onlineUsers.length;
  
  const isUserOnline = (userId) => {
    return onlineUsers.some(user => user.userId === userId);
  };
  
  const getUserByColor = (color) => {
    return onlineUsers.find(user => user.cursorColor === color);
  };
  
  const getOnlineUserIds = () => {
    return onlineUsers.map(user => user.userId);
  };

  return {
    onlineUsers,
    onlineCount,
    isUserOnline,
    getUserByColor,
    getOnlineUserIds
  };
}

export default usePresence;
